import { IconHelp, Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, ReactNode, isValidElement, memo, useState } from 'react';
import styles from './CardTable.module.scss';
import { useIsClamped } from '@/hooks/useIsClamped';
import { useGlobalContainerRef } from '@/wc/helper/ref';

const cx = classnames.bind(styles);

export type Props = {
  label: ReactNode;
  value: ReactNode;
  align?: 'left' | 'right';
  unit?: ReactNode;
  caption?: ReactNode;
  help?: ReactNode;
};

/**
 * Column `render()` functions shared with the Table layout often wrap plain
 * values in a `<TooltipCell title={...}>` element before they reach `Cell`.
 * Rendering that element again inside our own tooltip would nest a
 * second, always-on hover tooltip inside this one, so prefer its `title`
 * (the original plain value) when present.
 */
const getTooltipValue = (value: ReactNode): ReactNode =>
  isValidElement<{ title?: ReactNode }>(value) && 'title' in value.props
    ? value.props.title
    : value;

export const Cell: FC<Props> = memo(
  ({ label, value, align = 'left', unit, caption, help }) => {
    // Unwrap a `<TooltipCell title={...}>` (shared with the Table layout)
    // to its plain content: rendering it as-is would nest its own
    // always-mounted `Tooltip` inside `valueNode`, which fires on hover of
    // the same spot and covers this component's unified tooltip.
    const displayValue = getTooltipValue(value);
    const { ref, isClamped } = useIsClamped<HTMLDivElement>(value);
    const { ref: labelRef, isClamped: isLabelClamped } =
      useIsClamped<HTMLSpanElement>(label);
    const { ref: captionRef, isClamped: isCaptionClamped } =
      useIsClamped<HTMLSpanElement>(caption);
    // `mouseenter` fires on the outer cell div too when the pointer lands
    // directly on the nested help icon (browsers fire it on every ancestor
    // being entered, not just the target), so the outer Tooltip would open
    // at the same time as the help icon's own — suppress it explicitly
    // while the help icon is hovered.
    const [isHelpHovered, setIsHelpHovered] = useState(false);
    // NA-5596: without an explicit container, antd defaults to the trigger's
    // parentNode, which sits inside `.grid`'s `overflow: hidden` — clipping
    // the tooltip at the card boundary instead of letting it float freely.
    const getPopupContainer = useGlobalContainerRef();

    const labelNode = (
      <span ref={labelRef} className={cx('label-text')}>
        {label}
      </span>
    );
    const valueNode = (
      <div ref={ref} className={cx('value', { withCaption: Boolean(caption) })}>
        {displayValue}
      </div>
    );
    const captionNode = caption && (
      <span ref={captionRef} className={cx('caption')}>
        {caption}
      </span>
    );

    // NA-5596: label + value + code truncation share one tooltip content —
    // "label: value(code)" — shown when any one of the three is truncated.
    // Per Figma, the label sits on its own line above value(code), not inline.
    const isTruncated = isClamped || isLabelClamped || isCaptionClamped;
    const tooltipTitle = isTruncated ? (
      <>
        {label}:<br />
        {displayValue}
        {caption ? <>({caption})</> : null}
      </>
    ) : undefined;

    // Columns that also carry a `help` tooltip (借方/貸方仕入控除税額) still
    // wrap the whole cell in the same unified truncation tooltip as every
    // other cell — only the help icon keeps its own separate, static
    // description tooltip nested inside, since that content is unrelated to
    // truncation and must stay reachable on its own.
    return (
      <Tooltip
        title={tooltipTitle}
        open={isHelpHovered ? false : undefined}
        getPopupContainer={getPopupContainer}
      >
        <div className={cx('cell')}>
          <dt className={cx('label')}>
            {labelNode}
            {help && (
              <Tooltip title={help} getPopupContainer={getPopupContainer}>
                <span
                  className={cx('label-help')}
                  onMouseEnter={() => setIsHelpHovered(true)}
                  onMouseLeave={() => setIsHelpHovered(false)}
                >
                  <IconHelp />
                </span>
              </Tooltip>
            )}
          </dt>
          <dd className={cx('content', { 'align-right': align === 'right' })}>
            <div className={cx('value-block')}>
              {valueNode}
              {captionNode}
            </div>
            {unit && <span className={cx('unit')}>{unit}</span>}
          </dd>
        </div>
      </Tooltip>
    );
  }
);
Cell.displayName = 'Cell';

/**
 * Empty cell padding out the last row. Renders borders only.
 */
export const FillerCell: FC = memo(() => (
  <div className={cx('cell')} aria-hidden='true' />
));
FillerCell.displayName = 'FillerCell';
